import { Component, Input, OnInit, TemplateRef } from '@angular/core';
import { DashboardEvent, UserGroup } from '@models';
import { Isal } from '@models/Isal/isal.model';
import { IsalIrrigationPlanInfo, IsalReleaseOptions } from '@models/Isal/isalIrrigationPlanInfo.model';
import { IsalService } from '@services/isal.service';
import { UserService } from '@services/user.service';
import { ChangeLogNotesComponent, ChangeLogNotesParams } from 'app/components/messages/changelog-notes/changelognotes.components';
import { CommonShared } from 'app/shared/classes/common';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';

@Component({
  standalone: false,
  selector: 'isal',
  templateUrl: './isal.component.html',
  styleUrls: ['./isal.component.scss']
})

export class IsalComponent implements OnInit {

  @Input() dashboardEvents: DashboardEvent[];
  @Input() associatedUserGroups: UserGroup[];

  public common = new CommonShared();
  
  modalDragOn: boolean = false;

  public isReadyIsalData: boolean = false;
  public isalData: Isal[] = [];
  public isalDataDisplayed: Isal[] = [];
  public isalDataSelected: Isal[] = [];

  public isalIrrigatePlan: IsalIrrigationPlanInfo;

  public username: string = '';
  public canUserViewIsal: boolean = false;

  public commentNames: string[] = [];
  public activityPhases: string[] = [];
  public storageLocationNames: string[] = [];
  public cropTypeNames: string[] = [];

  public selectedCommentNames: string[] = [];
  public selectedActivityPhases: string[] = [];
  public selectedStorageLocationNames: string[] = [];
  public selectedCropTypeNames: string[] = [];

  public plantingDateLow: number = 0;
  public plantingDateHigh: number = 500;
  public transPlantDateLow: number = 0;
  public transPlantDateHigh: number = 500;

  isalCols = [
    { field: 'checked', header: '', width: '5%' },
    { field: 'euid', header: 'EUID', width: '9%' },
    { field: 'storageContainer', header: 'Container', width: '7%' },
    { field: 'cropType', header: 'Crop', width: '8%' },
    { field: 'moisture', header: 'Moisture', width: '7%' },
    { field: 'alarmCount', header: 'Alarm#', width: '7%' },
    { field: 'lastWaterPlanElapsedDays', header: 'Last Water', width: '7%' },
    { field: 'plantingDateOffset', header: 'Age', width: '6%' },
    { field: 'activityPhase', header: 'Activity Phase', width: '8%' },
    { field: 'commentName', header: 'Comment', width: '17%' },
    { field: 'requestDescription', header: 'Request Description', width: '19%' },
  ];

  public irrigationExecutionOptions: string[] = [ 'Forecast', 'Released' ];
  public irrigationRigOptions: string[] = [
    'Watering Position Row 09 K1',
    'Watering Position Row 10 K1',
    'Watering Position Row 15 K2',
    'Watering Position Row 16 K2',
    'Watering Position Row 21 K3',
    'Watering Position Row 22 K3',
    'Watering Position Row 09 L1',
    'Watering Position Row 10 L1',
    'Watering Position Row 15 L2',
    'Watering Position Row 16 L2',
    'Watering Position Row 21 L3',
    'Watering Position Row 22 L3' ];

  public irrigateModalRef: BsModalRef;
  public logModalRef: BsModalRef;
  public changeLogNotes: string = "";

  constructor(public toastr: ToastrService, public modalService: BsModalService, public confirmationService: ConfirmationService,
              public _userService: UserService, public _isalService: IsalService) { }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {

        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.getUserRoleForISAL();

    if (this.canUserViewIsal) {
      this.loadIsalData();
    }
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  getUserRoleForISAL() {
    this.canUserViewIsal = (this.associatedUserGroups.findIndex(x => x.name.toLowerCase() == "isal") > -1) ? true : false;
  }


  loadIsalData() {
    this.isalData = [];
    this.isalDataDisplayed = [];
    this.isalDataSelected = [];

    var sensorGroupNames = this.dashboardEvents.map(x => x.eventConditions.map(y => y.sensorGroupName));
    var flattenSensorGroupNames = [].concat(...sensorGroupNames);

    // Remove duplicates from the list of sensor group names
    flattenSensorGroupNames = Array.from(new Set(flattenSensorGroupNames));

    
    this.getISALData(flattenSensorGroupNames);

    //this.getISALData([ "426495593", "426495594", "426495595", "426495596" ]);
  }


  getISALData(euids: string[]): void {
    this.isReadyIsalData = false;
    this._isalService.getIsalInfo(euids).subscribe(
      results => {
        if (results != null) {
          this.isalData = results;  // Currently not converting from IsalInfo to Isal models
          this.isalData.forEach(x => {
            x.checked = false;
            x.commentName = (x.commentName != undefined) ? x.commentName.toLowerCase() : 'null';
            x.activityPhase = (x.activityPhase != undefined) ? x.activityPhase.toLowerCase() : 'null';
            x.storageLocationName = (x.storageLocationName != undefined) ? x.storageLocationName : 'null';
            x.lastWaterPlanElapsedDays = (x.lastWaterPlanElapsedMinutes != undefined) ? this.common.setValuePrecisionForKey(x.lastWaterPlanElapsedMinutes/1440, 'lastWaterPlanElapsedHours') : undefined;
          });

          this.calculatePlantAndTransplantDateOffsets();
          this.buildSearchLists();
          this.combineDuplicateEntries();
          this.updateDisplayedISALDataOnSearchCriteria();
          this.updateISALDataWithDashboardEventData();
        }
        this.isReadyIsalData = true;
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving data from ISAL.');
        this.isReadyIsalData = true;
      });
  }


  combineDuplicateEntries() {
    this.isalData.sort((a, b) => {
      if (b.euid > a.euid) {
        return 1;
      }
      if (b.euid < a.euid) {
        return -1;
      }
    });

    var count = 0;
    while (count < this.isalData.length-1) {
      var isal = this.isalData[count];
      var isalNext = this.isalData[count+1];

      if (isal.euid == isalNext.euid) {
        isal.commentName += "|" + isalNext.commentName
        this.isalData.splice(count+1, 1);
      }
      else {
        count++;
      }
    }
  }


  calculatePlantAndTransplantDateOffsets() {
    this.isalData.forEach(x => {
      x.plantingDateOffset = (-1 * moment(x.plantingDate, "MM/DD/YYYY").local().diff(moment(),'days'));
      x.transplantingDateOffset = (-1 * moment(x.transplantingDate, "YYYY-MM-DDTHH:mm").local().diff(moment(),'days'));
      x.plantingDateOffset = x.plantingDateOffset < 1 ? 0 : x.plantingDateOffset;
      x.transplantingDateOffset = x.transplantingDateOffset < 1 ? 0 : x.transplantingDateOffset; 
    });
  }


  buildSearchLists() {
    this.commentNames = this.isalData.map(x => x.commentName);
    this.activityPhases = this.isalData.map(x => x.activityPhase);
    this.storageLocationNames = this.isalData.map(x => x.storageLocationName);
    this.cropTypeNames = this.isalData.map(x => x.cropType);

    this.commentNames = [... new Set(this.commentNames)];
    this.activityPhases = [... new Set(this.activityPhases)];
    this.storageLocationNames = [... new Set(this.storageLocationNames)];
    this.cropTypeNames = [... new Set(this.cropTypeNames)];

    this.selectedCommentNames = [...this.commentNames];
    this.selectedActivityPhases = [...this.activityPhases];
    this.selectedStorageLocationNames = [...this.storageLocationNames];
    this.selectedCropTypeNames = [...this.cropTypeNames];
  }


  updateDisplayedISALDataOnSearchCriteria() {
    // Updated displayed groups based on filter selection
    this.isalDataDisplayed = this.isalData.filter(x =>
      this.selectedCommentNames.some(y => x.commentName.split('|').includes(y)) &&
      this.selectedActivityPhases.includes(x.activityPhase) &&
      this.selectedCropTypeNames.includes(x.cropType) &&
      this.selectedStorageLocationNames.includes(x.storageLocationName) &&
      x.transplantingDateOffset >= this.transPlantDateLow &&
      x.transplantingDateOffset <= this.transPlantDateHigh &&
      x.plantingDateOffset >= this.plantingDateLow &&
      x.plantingDateOffset <= this.plantingDateHigh
    );

    // Uncheck groups that are not currently selected
    this.isalDataSelected.forEach(x => {
      if (!this.isalDataDisplayed.includes(x)) {
        x.checked = false;
      }
    });
  }


  updateISALDataWithDashboardEventData() {
    this.isalDataDisplayed.forEach(x => {
      this.dashboardEvents.forEach(y => {
        var eventCondition = y.eventConditions.find(z => z.sensorGroupName == x.euid);
        if (eventCondition != undefined) {
          x.moisture = Number(eventCondition.value);
        }
      });
    });
  }


  isalCheckedChanged() {
    this.isalDataSelected = this.isalDataDisplayed.filter(x => x.checked == true);
  }


  toggleSelectAll() {
    if (this.isalDataDisplayed == undefined || this.isalDataDisplayed.length < 1) {
      return;
    }
    var newState = !this.isalDataDisplayed[0].checked;
    this.isalDataDisplayed.forEach(sensor => sensor.checked = newState );

    this.isalCheckedChanged();
  }


  createISALActivityPlan(template: TemplateRef<any>) {
    this.isalIrrigatePlan = {
      description: "",
      duration: 0,
      startOn: moment().format("YYYY-MM-DDTHH:mm"),
      wateringRig: "",
      euids: this.isalDataSelected.map(x => x.euid),
      release: false,
      releaseOption: IsalReleaseOptions.Forecast
    }
    this.changeLogNotes = '';
    this.irrigateModalRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );
  }


  sendISALActivityPlan() {
    if (this.isalIrrigatePlan.startOn == '') {
      this.isalIrrigatePlan.startOn = moment().format("YYYY-MM-DDTHH:mm");
    }

    if (!moment(this.isalIrrigatePlan.startOn).isValid()) {
      this.toastr.error('Start date/time is not valid. Try using: yyyy-mm-dd hh:mm');
      return;
    }

    if (this.isalIrrigatePlan.description == '') {
      this.toastr.error('A description must be provided.');
      return;
    }

    if (this.isalIrrigatePlan.duration < 1) {
      this.toastr.error('Duration must be greater than 0.');
      return;
    }

    this.irrigateModalRef.hide();

    this.isalIrrigatePlan.startOn = `/Date(${parseInt((moment.utc(this.isalIrrigatePlan.startOn).toDate().getTime()).toFixed(0))})/`;
    this.isalIrrigatePlan.release = (this.isalIrrigatePlan.releaseOption == IsalReleaseOptions.Forecast) ? false : true;

    this._isalService.createIsalIrrigationPlan(this.isalIrrigatePlan, this.username).subscribe(
      results => {
        if (results != null) {
          this.toastr.success('Plan Created');
        } else {
          this.toastr.error('Unable to send watering plan to ISAL. -> ' + results);
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while sending watering plan to ISAL.');
      });
  }


  addNotes2ChangeLog() {
    this.changeLogNotes = '';
    
    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: 0,
      category: ''
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }
  

}
